import { useEffect } from "react";
import { Link } from "react-router-dom";
import HeroStagger from "../components/HeroStagger";
import HeroBackdrop from "../components/HeroBackdrop";
import Reveal from "../components/Reveal";
import Counter from "../components/Counter";
import VideoFrame from "../components/VideoFrame";
import { PROJECTS } from "../lib/projects";

const TIDYCAL = "/book";

function scrollToHow(e) {
  e.preventDefault();
  const el = document.getElementById("how-it-works");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function PortfolioPreviewCard({ project, index }) {
  return (
    <article className="work-card" data-testid={`home-work-card-${index}`}>
      <VideoFrame src={project.video} title={project.title} aspect="aspect-video" />
      <div className="p-6 md:p-7">
        <div className="flex items-center justify-between mb-3">
          <span className="eyebrow">{project.category}</span>
          <span className="text-xs uppercase tracking-widest text-teal">Live</span>
        </div>
        <h3 className="display-h3 mb-3">{project.title}</h3>
        <p className="text-muted text-sm leading-relaxed">{project.shortDescription}</p>
      </div>
    </article>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = "Socialize Video — Built in a session. Built to last.";
  }, []);

  return (
    <div className="bg-bg" data-testid="home-page">
      {/* SECTION 1 — HERO */}
      <section className="relative min-h-[88vh] md:min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
        <HeroBackdrop />
        <div className="container-x w-full grid grid-cols-12 gap-8 py-24 md:py-32 relative z-10">
          <div className="col-span-12 md:col-span-11 lg:col-span-10">
            <div className="eyebrow mb-6" data-testid="hero-eyebrow">Built in a session. Built to last.</div>

            <h1 className="display-hero">
              <HeroStagger lines={[
                "Your business online.",
                "Done right.",
                "Done fast.",
              ]} />
            </h1>

            <p className="mt-7 max-w-2xl text-lg md:text-xl text-ink/80 leading-relaxed" data-testid="hero-subheadline">
              I build custom websites and AI-powered tools for small businesses — usually in a single session. Starting at <span className="text-ink font-semibold">$3,000</span>.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-6">
              <a href="#how-it-works" onClick={scrollToHow} className="cta" data-testid="hero-cta">
                See How It Works <span aria-hidden="true">→</span>
              </a>
              <Link to="/apply" className="cta-ghost" data-testid="hero-apply-link">
                Apply for a build <span aria-hidden="true">→</span>
              </Link>
            </div>

            <p className="mt-10 text-sm text-muted max-w-xl" data-testid="hero-credibility">
              20 years in marketing and video production. Now I build what used to take months — in a day.
            </p>
          </div>
        </div>

        {/* Bottom decorative hairline + scroll hint */}
        <div className="absolute bottom-8 left-0 right-0 container-x flex items-center justify-between text-xs text-muted z-10">
          <span className="hidden md:inline">socialize.video</span>
          <span className="flex items-center gap-2">
            <span className="h-px w-8 bg-muted/40" />
            scroll
          </span>
        </div>
      </section>

      {/* SECTION 2 — THE PROBLEM */}
      <section className="py-28 md:py-36" data-testid="problem-section">
        <div className="container-x grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-2">
            <div className="eyebrow">01 — The problem</div>
          </div>
          <div className="col-span-12 md:col-span-9 lg:col-span-8">
            <Reveal as="h2" className="display-h2 mb-10">
              Most small business websites are slow, generic, or never finished.
            </Reveal>
            <div className="space-y-6 text-lg text-ink/85 leading-relaxed max-w-3xl">
              <Reveal as="p">You hired someone and waited months. Or you tried a template and it never felt right. Or it's just sitting there, half-done, not doing anything for your business.</Reveal>
              <Reveal as="p">Your site should capture leads, build trust, and work while you sleep. It should actually feel like you.</Reveal>
              <Reveal as="p">Your business deserves better than a template. It deserves someone who actually knows marketing.</Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — HOW IT WORKS */}
      <section id="how-it-works" className="py-24 md:py-32 bg-surface" data-testid="how-it-works-section">
        <div className="container-x">
          <div className="grid grid-cols-12 gap-8 mb-16">
            <div className="col-span-12 md:col-span-2">
              <div className="eyebrow">02 — How it works</div>
            </div>
            <div className="col-span-12 md:col-span-10">
              <Reveal as="h2" className="display-h2 max-w-2xl">How a Build Works</Reveal>
            </div>
          </div>

          <Reveal staggerChildrenMs={80} className="grid grid-cols-1 md:grid-cols-3 gap-px bg-hair/15 hairline">
            {[
              { n: "01", t: "You apply.", b: "Tell me about your business and what you need. I review every application personally and confirm it's a fit." },
              { n: "02", t: "We build.", b: "In a dedicated session, we build your site or tool — you're involved, you see it come together in real time." },
              { n: "03", t: "You own it.", b: "Full handover. No monthly fees. No platform lock-in. No dependency on me to make changes." },
            ].map((s, i) => (
              <div key={s.n} className="reveal bg-surface p-8 md:p-10" data-testid={`how-step-${i + 1}`}>
                <div className="font-display text-teal text-4xl md:text-5xl font-bold mb-8">{s.n}</div>
                <h3 className="display-h3 mb-4">{s.t}</h3>
                <p className="text-muted text-base leading-relaxed">{s.b}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* SECTION 4 — PORTFOLIO PREVIEW */}
      <section className="py-28 md:py-36" data-testid="portfolio-preview-section">
        <div className="container-x">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-2">
              <div className="eyebrow">03 — Recent</div>
            </div>
            <div className="col-span-12 md:col-span-10 flex items-end justify-between flex-wrap gap-6">
              <Reveal as="h2" className="display-h2">Recent Builds</Reveal>
              <Link to="/work" className="cta-ghost" data-testid="home-see-all-work">See all builds <span aria-hidden="true">→</span></Link>
            </div>
          </div>

          <Reveal staggerChildrenMs={100} className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {PROJECTS.filter((p) => p.status === "Live").slice(0, 2).map((p, i) => (
              <div key={p.slug} className="reveal">
                <PortfolioPreviewCard project={p} index={i} />
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* SECTION 5 — THE BUILDER */}
      <section className="py-28 md:py-36 bg-surface" data-testid="builder-section">
        <div className="container-x grid grid-cols-12 gap-10 md:gap-16">
          <div className="col-span-12 md:col-span-7">
            <div className="eyebrow mb-6">04 — The builder</div>
            <Reveal as="h2" className="display-h2 mb-8">Who Builds This</Reveal>

            <div className="flex items-center gap-4 mb-7" data-testid="builder-portrait">
              <img
                src="/jeff.jpg"
                alt="Jeff Kohler, founder of Socialize Video"
                className="h-14 w-14 md:h-16 md:w-16 rounded-full object-cover border border-ink/15"
                loading="lazy"
                width="64"
                height="64"
              />
              <div className="leading-tight">
                <div className="font-display font-bold text-ink text-base md:text-lg">Jeff Kohler</div>
                <div className="text-sm text-muted">Founder, Socialize Video</div>
              </div>
            </div>

            <div className="space-y-5 text-lg text-ink/85 leading-relaxed max-w-xl">
              <Reveal as="p">I'm Jeff Kohler. For 20 years I've worked in marketing and video production — building brands, producing content, running campaigns.</Reveal>
              <Reveal as="p">Now I use AI to build websites and business tools that used to take months. I take on a limited number of projects so every build gets my full attention.</Reveal>
              <Reveal as="p">This isn't a team. It isn't outsourced. It's me.</Reveal>
            </div>
          </div>

          <div className="col-span-12 md:col-span-5">
            <div className="grid grid-cols-2 gap-px bg-hair/15 hairline">
              {[
                { value: 20, prefix: "", suffix: "", label: "Years in marketing" },
                { value: 3, prefix: "$", suffix: "K", label: "Starting price" },
                { value: 1, prefix: "", suffix: "", label: "Session to build" },
                { value: 100, prefix: "", suffix: "%", label: "You own it" },
              ].map((s, i) => (
                <div key={i} className="bg-surface p-6 md:p-8" data-testid={`builder-stat-${i + 1}`}>
                  <div className="font-display text-ink text-5xl md:text-6xl font-bold leading-none mb-3">
                    <Counter end={s.value} prefix={s.prefix} suffix={s.suffix} />
                  </div>
                  <div className="text-sm text-muted">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — SELECTIVE INTAKE */}
      <section className="section-dark py-32 md:py-40" data-testid="selective-intake-section">
        <div className="container-x">
          <Reveal className="max-w-2xl mx-auto text-center">
            <div className="eyebrow mb-6">05 — Limited intake</div>
            <h2 className="display-h2 mb-7">I take on a limited number of projects.</h2>
            <p className="text-lg text-ink/85 leading-relaxed mb-10">
              I typically take on 2–4 builds per month. Each one gets my full focus — strategy, design, build, and handover.
            </p>
            <p className="text-base text-muted mb-10">
              If that sounds like what you need, apply. The form takes 3 minutes.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
              <Link to="/apply" className="cta" data-testid="intake-apply-cta">
                Apply for a Build <span aria-hidden="true">→</span>
              </Link>
              <a href={TIDYCAL} target="_blank" rel="noopener noreferrer" className="cta-ghost" data-testid="intake-call-link">
                Book a discovery call <span aria-hidden="true">→</span>
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
